import { latestReports } from "@/mocks/site";
import Reveal from "@/pages/home/components/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { categoryBadgeClass, riskBadgeClass, riskDotClass } from "@/pages/home/utils";

interface RadarSectionProps {
  onJumpCategory: (key: string) => void;
}

export default function RadarSection({ onJumpCategory }: RadarSectionProps) {
  const featured = latestReports.slice(0, 2);
  const list = latestReports.slice(2);

  return (
    <section id="radar" className="w-full bg-background-50 px-4 py-16 md:px-8 md:py-24">
      <div className="mx-auto w-full max-w-6xl">
        <SectionHeading
          eyebrow="THIS WEEK RADAR"
          title="本周避雷雷达"
          desc="把过去七天投稿量增长最快、问题描述最具体的几条争议挑出来，先看这些，再决定要不要下单。"
        />

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="flex flex-col gap-6 lg:col-span-7">
            {featured.map((report, index) => (
              <Reveal key={report.id} delay={index * 90}>
                <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 md:flex-row">
                  <div className="relative h-52 w-full shrink-0 overflow-hidden md:h-auto md:w-64">
                    <img
                      src={report.cover}
                      alt={`${report.brandName} 相关避雷速报配图`}
                      title={`${report.brandName}｜${report.title}`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className={`absolute left-3 top-3 rounded-full border px-2.5 py-1 font-label text-xs ${riskBadgeClass(report.riskLevel)}`}>
                      风险 {report.riskLevel}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5 md:p-6">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`rounded px-2 py-0.5 font-label text-xs ${categoryBadgeClass(report.category)}`}>
                        {report.categoryLabel}
                      </span>
                      <span className="font-label text-xs text-foreground-500">{report.date}</span>
                    </div>
                    <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">{report.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">{report.excerpt}</p>
                    <button
                      type="button"
                      onClick={() => onJumpCategory(report.category)}
                      className="mt-4 flex cursor-pointer items-center gap-1 self-start whitespace-nowrap font-label text-sm text-primary-600 transition-colors hover:text-primary-700"
                    >
                      查看同类档案
                      <i className="ri-arrow-right-line text-base leading-none"></i>
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120} className="lg:col-span-5">
            <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <div className="flex items-center justify-between border-b border-background-200 pb-4">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-list-check-2 text-lg leading-none text-primary-600"></i>
                  速报滚动
                </h3>
                <span className="font-label text-xs text-foreground-500">近 7 天</span>
              </div>
              <ul className="mt-2 flex flex-col divide-y divide-background-200">
                {list.map((report) => (
                  <li key={report.id} className="flex gap-4 py-4">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md">
                      <img
                        src={report.cover}
                        alt={`${report.brandName} 速报缩略图`}
                        title={`${report.brandName}｜避雷速报`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${riskDotClass(report.riskLevel)}`}></span>
                        <span className="truncate font-label text-xs text-foreground-500">
                          {report.brandName} · {report.categoryLabel}
                        </span>
                      </div>
                      <p className="mt-1.5 line-clamp-2 text-sm font-medium leading-snug text-foreground-900">{report.title}</p>
                      <span className="mt-1.5 font-label text-xs text-foreground-400">{report.date}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => onJumpCategory("all")}
                className="mt-auto flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                浏览全部档案
                <i className="ri-arrow-down-line text-base leading-none"></i>
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}